import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  TDropoffCalculatorResponse,
  TDropOffTag,
} from "domain/entities/dropoff-calculator-response";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { SegmentContext } from "./SegmentProvider";
import { createBlobDownloader } from "../../../utils/files/downloadBlob";
import { checkParamsFromCreative } from "helper/filtersHelper";

type DropoffCalculatorProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
};

export type TSelectOption = {
  value: any;
  label: string;
};

export type TDropoffCalculatorState = {
  requestParamsIndex: number;
  dropOffItems: (TDropOffItem | null)[];
  allTags: TDropOffTag[];
  range?: string;
  dropOffSelectOptions: TSelectOption[];
  setSelectedItemByIndex: (option: TSelectOption, index: number) => void;
  removeItem: (index: number) => void;
  dropOffState: (TSelectOption | null)[];
  maxTagsNumber: number;
  addNewItem: () => void;
  downloadDropOffCSV: () => void;
  downloadingReports?: boolean;
  isLoading: boolean;
};

export type TDropOffItem = {
  tag: TDropOffTag;
  dropOffPercentage?: number;
  selectedOption: TSelectOption;
};

const INITIAL_LIMIT_OF_TAG_ROWS = 5;

const initContext: TDropoffCalculatorState = {
  requestParamsIndex: 0,
  dropOffSelectOptions: [],
  dropOffItems: [],
  allTags: [],
  setSelectedItemByIndex: (option: TSelectOption, index: number) => null,
  downloadDropOffCSV: () => null,
  removeItem: (index: number) => null,
  dropOffState: [],
  maxTagsNumber: INITIAL_LIMIT_OF_TAG_ROWS,
  addNewItem: () => null,
  isLoading: true,
};

export const DropoffCalculatorContext = createContext(initContext);

export const DropoffCalculatorProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
}: DropoffCalculatorProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dropOffData, setDropOffData] = useState<TDropoffCalculatorResponse>();
  const [dropOffState, setDropOffState] = useState<(TSelectOption | null)[]>(
    []
  );
  const { segment } = useContext(SegmentContext);

  const maxTagsNumber = useMemo(
    () => dropOffData?.dropoffData.length || INITIAL_LIMIT_OF_TAG_ROWS,
    [dropOffData]
  );

  useEffect(() => {
    if (maxTagsNumber && !dropOffState.length) {
      setDropOffState(new Array(maxTagsNumber).fill(null));
    }
  }, [maxTagsNumber]);

  const allTags = useMemo(() => dropOffData?.dropoffData || [], [dropOffData]);
  const range = useMemo(() => dropOffData?.range || "", [dropOffData]);

  const findTagById = useCallback(
    (tagId) => {
      return allTags.find(({ id }) => tagId === id)!;
    },
    [allTags]
  );

  const calculatePercentage = useCallback(
    (lastTagQuantity: number, currentTagQuantity: number) => {
      return (
        ((lastTagQuantity - currentTagQuantity) / lastTagQuantity) *
        100
      ).toFixed(0);
    },
    []
  );

  const dropOffItems = useMemo<(TDropOffItem | null)[]>(() => {
    let lastTag: TDropOffTag | undefined = undefined;

    return dropOffState.reduce<(TDropOffItem | null)[]>(
      (acc, selectOption, index) => {
        if (!selectOption) return [...acc, null];
        const tag = findTagById(selectOption.value);
        const item = {
          tag,
          selectedOption: selectOption,
          dropOffPercentage: lastTag
            ? calculatePercentage(lastTag.quantity, tag.quantity)
            : null,
        };
        lastTag = tag;
        return [...acc, item] as (TDropOffItem | null)[];
      },
      []
    );
  }, [dropOffState]);

  const dropOffSelectOptions = useMemo(
    () =>
      allTags.map(({ id, description }) => ({
        value: id,
        label: description,
      })),
    [allTags]
  );

  useEffect(() => {
    setDropOffState((state) => state.map(() => null));
  }, [allTags]);

  const setSelectedItemByIndex = (option: TSelectOption, index: number) => {
    const alreadyUsedTagIndex = dropOffState.findIndex(
      (select) => option.value === select?.value
    );
    const newState = [...dropOffState];

    if (alreadyUsedTagIndex !== -1) {
      newState[alreadyUsedTagIndex] = null;
    }

    newState[index] = option;
    setDropOffState(newState);
  };

  const addNewItem = () => {
    segment.track("[DropOff Calculator] Add item");
    setDropOffState((options) => [...options, null]);
  };

  const removeItem = (index: number) => {
    segment.track("[DropOff Calculator] Remove item");
    setDropOffState([
      ...dropOffState.slice(0, index),
      ...dropOffState.slice(index + 1),
    ]);
  };

  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsFromCreative);
    }

    return checkParamsFromCreative(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        .getDropOffs(currentRequestParams)
        .then(setDropOffData)
        .then(() => setIsLoading(false));
    }
  }, [currentRequestParams]);

  const [downloadingReports, setDownloadingReports] = useState<boolean>(false);

  const downloadDropOffCSV = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportDropOffCSV({
        ...currentRequestParams,
        dataExport: dropOffItems
          .filter((item) => !!item)
          .map((item) => ({
            flowStep: item?.tag.description || "",
            respondents: item?.tag.quantity.toString() || "",
            dropoff: item?.dropOffPercentage
              ? `${item.dropOffPercentage}%`
              : "",
          })),
      })
      .then(createBlobDownloader("dropoff.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams, dropOffItems]);

  return (
    <DropoffCalculatorContext.Provider
      value={{
        requestParamsIndex,
        dropOffItems,
        allTags,
        range,
        dropOffSelectOptions,
        setSelectedItemByIndex,
        removeItem,
        dropOffState,
        maxTagsNumber,
        downloadDropOffCSV,
        addNewItem,
        downloadingReports,
        isLoading,
      }}
    >
      {canShowPanel ? children : <></>}
    </DropoffCalculatorContext.Provider>
  );
};
